import { useCallback, useEffect, useRef } from 'react';
import { splitSentences } from '../../utils/sentenceSplitter';
import { readSSEStream } from '../../utils/sseStream';
import useAudioNarration from './useAudioNarration';

/**
 * Sentinel the interviewer emits in its closing message to signal the interview
 * is over. It is a control token, not content: it must never be rendered and
 * must never be handed to text-to-speech (otherwise the interviewer literally
 * says "end here" as its parting words). Completion is reported out-of-band via
 * the `interviewComplete` flag instead of by substring-matching the transcript.
 */
// `END\s+HERE`, not a literal space: the prompt asks the model to put the
// sentinel "on its own at the very end", which routinely produces a newline
// between the two words. A literal-space pattern misses that, and the miss is
// doubly bad — the interviewer speaks "END HERE" aloud AND the interview never
// registers as complete, so feedback is never generated.
const END_SENTINEL = /\s*END\s+HERE\s*/gi;

const hasEndSentinel = (text) => /END\s+HERE/i.test(text || '');

// KNOWN LIMITATION, deliberately not "fixed": if the closing message is cut off
// part-way through the sentinel (a max_tokens cut, a proxy truncating), the
// partial tail — "…thanks. END H" — is displayed and spoken. Suppressing
// partial tails requires treating a trailing "end" as provisional, which would
// corrupt the far more common legitimate sentence ending in that word ("…and
// that was the end"). Leaking a fragment on a rare truncation is the better
// failure. The sentinel arriving split across DELTAS is handled — that case is
// resolved by stripping the accumulated buffer, see stripEndSentinel below.

/**
 * Remove the sentinel, collapsing the whitespace that surrounded it.
 * Deliberately does NOT trim the tail: this runs on a partially-received
 * buffer, and eating a trailing space would splice the next chunk's first word
 * onto the previous one ("thank you" + "for" -> "thank youfor").
 */
const stripEndSentinel = (text) => (text || '').replace(END_SENTINEL, ' ');

/**
 * Streaming AI responses for the behavioral interview.
 *
 * Owns the SSE transport and the behavioral module's content policy (the END
 * HERE sentinel). Audio playback, the synthesis queue and the duration-synced
 * character reveal live in useAudioNarration, which the technical interview
 * drives from its own SSE loop — see that file for why the split exists.
 *
 * @param {Object} options
 * @param {boolean} options.speechEnabled - Whether audio mode is enabled
 * @param {Function} options.generateAudio - Function to generate TTS audio (from useTextToSpeech)
 * @param {Function} options.onError - Error callback
 * @returns {Object} Streaming controls
 */
const useStreamingResponse = (options = {}) => {
  const { speechEnabled = false, generateAudio, onError } = options;

  // Refs
  const streamingControllerRef = useRef(null);
  const speechEnabledRef = useRef(speechEnabled);

  // The narration engine reports through whatever callbacks the current turn
  // was started with; these refs forward to them without re-creating the hook.
  const updateRef = useRef(null);
  const speakingRef = useRef(null);

  const handleNarrationUpdate = useCallback((update) => updateRef.current?.(update), []);
  const handleSpeakingChange = useCallback((speaking) => speakingRef.current?.(speaking), []);

  const {
    enqueue,
    finish,
    stop: stopNarration,
    reset: resetNarration,
    getSentences,
  } = useAudioNarration({
    generateAudio,
    onUpdate: handleNarrationUpdate,
    onSpeakingChange: handleSpeakingChange,
  });

  // Keep speechEnabled in sync with ref
  useEffect(() => {
    speechEnabledRef.current = speechEnabled;
  }, [speechEnabled]);

  /**
   * Stop any interview audio that is playing or queued.
   */
  const stopAudio = useCallback(() => {
    stopNarration();
  }, [stopNarration]);

  /**
   * Abort current streaming request and silence any audio it started.
   */
  const abort = useCallback(() => {
    if (streamingControllerRef.current) {
      streamingControllerRef.current.abort();
      streamingControllerRef.current = null;
    }
    stopNarration();
  }, [stopNarration]);

  /**
   * Process streaming response in non-audio mode
   * @param {Response} response - Fetch response with SSE stream
   * @param {Function} updateCallback - Callback to update message state
   * @returns {Promise<Object>} Result with fullText and success status
   */
  const handleNonAudioMode = useCallback(
    async (response, updateCallback) => {
      let fullText = '';
      let receivedStreamEnd = false;
      let result = null;

      // Initialize empty message
      updateCallback({ content: '', isStreaming: true, fullText: '' });

      try {
        await readSSEStream(response, (data) => {
          if (data.type === 'text_chunk') {
            fullText += data.content;
            // Strip the sentinel from what is displayed so it never
            // flashes on screen mid-stream.
            updateCallback({
              content: stripEndSentinel(fullText).trimEnd(),
              fullText,
              isStreaming: true,
            });
            return true;
          }

          if (data.type === 'stream_end') {
            receivedStreamEnd = true;

            const visibleText = stripEndSentinel(fullText);
            // A clean stream_end carrying no text is still a dead end for
            // the user — say so rather than leaving a blank bubble that
            // looks like it is still loading.
            updateCallback({
              content: visibleText.trim() || '[Response ended unexpectedly]',
              fullText,
              isStreaming: false,
              interviewComplete: hasEndSentinel(fullText),
            });
            result = {
              fullText,
              success: true,
              interviewComplete: hasEndSentinel(fullText),
            };
            return false;
          }

          if (data.type === 'error') {
            // Clear isStreaming, otherwise the "responding…" indicator
            // pulses forever and the audio toggle stays latched off.
            updateCallback({
              content: stripEndSentinel(fullText),
              fullText,
              isStreaming: false,
            });
            onError?.(data.content || 'Stream error occurred');
            result = { fullText, success: false, error: data.content };
            return false;
          }

          return true;
        });

        if (result) return result;
      } finally {
        // Handle premature stream end.
        //
        // Gated on `result`, not on `receivedStreamEnd`: the error branch sets
        // result but not receivedStreamEnd, so keying on the latter let this
        // block overwrite the error update and tell the user the connection
        // dropped when the server had actually reported something specific
        // (a rate limit, an upstream failure).
        if (!result) {
          const finalText = stripEndSentinel(fullText).trim() || '[Response ended unexpectedly]';
          updateCallback({
            content: `${finalText}\n\n_(connection lost, response may be incomplete)_`,
            fullText,
            isStreaming: false,
          });
        }
      }

      return {
        fullText,
        success: receivedStreamEnd,
        interviewComplete: receivedStreamEnd && hasEndSentinel(fullText),
      };
    },
    [onError]
  );

  /**
   * Process streaming response in audio mode.
   *
   * Only the transport and the sentinel policy live here; sentences are handed
   * to the narration engine as they complete and it owns everything after that.
   *
   * @param {Response} response - Fetch response with SSE stream
   * @param {Function} updateCallback - Callback to update message state
   * @param {Function} onSpeakingChange - Callback when speaking state changes
   * @returns {Promise<Object>} Result with fullText and success status
   */
  const handleAudioMode = useCallback(
    async (response, updateCallback, onSpeakingChange) => {
      updateRef.current = updateCallback;
      speakingRef.current = onSpeakingChange;

      let textBuffer = '';
      // Raw text as received, sentinel included — used only for completion
      // detection. `textBuffer` holds the sentinel-free text that gets spoken.
      let rawText = '';
      let receivedStreamEnd = false;
      let result = null;

      // Initialize empty message
      updateCallback({ content: '', isStreaming: true, sentences: [] });

      try {
        await readSSEStream(response, (data) => {
          if (data.type === 'text_chunk') {
            rawText += data.content;
            // Strip on the ACCUMULATED buffer, not per chunk: the
            // sentinel routinely arrives split across deltas ("END " +
            // "HERE"), which a per-chunk strip would miss entirely.
            textBuffer = stripEndSentinel(textBuffer + data.content);

            // Extract complete sentences. See utils/sentenceSplitter: the
            // previous inline scan split inside decimals ("3.5 years" was
            // spoken as "3." then "5 years") and silently discarded fragments
            // below a length threshold while still advancing past them.
            const { sentences: complete, rest } = splitSentences(textBuffer);
            textBuffer = rest;

            complete.forEach(enqueue);
            return true;
          }

          if (data.type === 'stream_end') {
            receivedStreamEnd = true;

            // Handle remaining text. Strip again: when the sentinel is the
            // last thing the model emits, it is exactly this trailing
            // remainder that would otherwise be spoken aloud.
            const trailing = stripEndSentinel(textBuffer).trim();
            if (trailing.length > 0) enqueue(trailing);

            updateCallback({
              isStreaming: false,
              sentences: getSentences(),
              interviewComplete: hasEndSentinel(rawText),
            });

            result = {
              fullText: rawText,
              success: true,
              interviewComplete: hasEndSentinel(rawText),
            };
            return false;
          }

          if (data.type === 'error') {
            // Clear isStreaming here too — otherwise the audio toggle
            // stays disabled for the rest of the session.
            updateCallback({ isStreaming: false, sentences: getSentences() });
            onError?.(data.content || 'Stream error occurred');
            result = {
              fullText: rawText,
              success: false,
              error: data.content,
            };
            return false;
          }

          return true;
        });
      } finally {
        // Starts playback if it has not begun, and guarantees the transcript
        // ends up fully visible even when no audio ever arrived.
        finish();

        // If the stream ended without a stream_end frame (abort, network drop,
        // or a final frame split across reads), nothing else in audio mode ever
        // clears isStreaming — the "responding…" dots would pulse forever and
        // the Audio toggle would stay latched off for the rest of the session.
        if (!receivedStreamEnd) {
          updateCallback({ isStreaming: false, sentences: getSentences() });
        }
      }

      return (
        result || {
          fullText: rawText,
          success: false,
          interviewComplete: hasEndSentinel(rawText),
        }
      );
    },
    [enqueue, finish, getSentences, onError]
  );

  /**
   * Process streaming response
   * @param {Response} response - Fetch response with SSE stream
   * @param {Function} updateCallback - Callback to update message state
   * @param {Function} onSpeakingChange - Callback when speaking state changes (audio mode)
   * @returns {Promise<Object>} Result with fullText and success status
   */
  const streamResponse = useCallback(
    async (response, updateCallback, onSpeakingChange) => {
      // Note: Don't abort here - the response is already received
      // The abort controller is managed by getAbortSignal for the fetch request

      // A new turn is starting: clear any cancellation left over from the
      // previous one, otherwise this turn's audio would be dropped on arrival.
      resetNarration();

      try {
        if (speechEnabledRef.current) {
          return await handleAudioMode(response, updateCallback, onSpeakingChange);
        } else {
          return await handleNonAudioMode(response, updateCallback);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Streaming error:', error);
          onError?.(error.message);
          throw error;
        }
        return { fullText: '', success: false, aborted: true };
      }
    },
    [handleAudioMode, handleNonAudioMode, onError, resetNarration]
  );

  /**
   * Create AbortController signal for fetch requests
   * @returns {AbortSignal} Signal to pass to fetch
   */
  const getAbortSignal = useCallback(() => {
    abort();
    streamingControllerRef.current = new AbortController();
    return streamingControllerRef.current.signal;
  }, [abort]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abort();
    };
  }, [abort]);

  return {
    // Controls
    streamResponse,
    abort,
    stopAudio,
    getAbortSignal,
  };
};

export default useStreamingResponse;
